/* FLG MAP v9. Design rationale lives in assets/flg-map.css and
   assets/flg-map-phone.css. This header carries only the rules
   that will bite whoever edits this file.

   1. sizeUp DOES NOT SUBTRACT THE STICKY HEADER. docTop is the
      split's distance from the top of the DOCUMENT and already
      includes the call bar, the site header and the filter bar.
      v4 subtracted the header again on top of that and threw
      away 150px: 779px of map in a 1229px viewport instead of
      905. Do not reintroduce a stickyHeader term. The 45 percent
      cap protects a page where the map sits far down.

   2. GRID IGNORES THE MAP BOUNDS. In grid the map is
      display:none and its bounds are stale. Unguarded, a visitor
      who zoomed into Florida then hit Grid would see six of nine
      parcels on a page titled All listings. inView returns the
      list untouched in grid.

   3. SPLIT DOES NOT EXIST ON A PHONE. A draggable map above a
      scrolling list traps the page scroll.

   4. LEAVING GRID REMEASURES. A display:none map has a zero
      sized container, so invalidateSize comes first.

   5. THE COUNT, THE EXPAND CONTROL AND THE FILTERS COLLAPSE ARE
      BUILT HERE, NOT IN THE LIQUID. They enhance a map that does
      not work without JavaScript anyway, and building them here
      is why the whole UI pass shipped as asset files. Keep it
      that way: it is a 1KB tweak instead of an 18KB rewrite.

   6. STATE FILTER, v7. fm-state is optional markup: the liquid
      only prints it when the collection spans more than one
      state, so every reference to it here must tolerate a null.
      It filters on it.stt, which is the tail of the county
      field, the same split region() has always used.

      syncCounties IS THE POINT OF IT. Choosing a state hides the
      county options belonging to other states and clears the
      county if it no longer belongs. Without that a buyer can
      assemble State: Illinois plus County: Marion, get zero
      results, and have to work out for themselves which of the
      two controls is lying. Two filters over the same dimension
      have to agree with each other or they are worse than one.

   7. THE PHONE OVERRIDE IS NOW REVERSIBLE. v8, 2026-09-11.

      Rule 3 forces split to the phone view under 750px. The old
      resize handler did that and nothing else:

        if(mode() === 'split' && isPhone()) setMode('map');
        else sizeUp();

      Narrowing the window flipped split to map. Widening it back
      then found mode() === 'map', fell to the else, and only
      resized. A one way door: drag a desktop window narrow once
      and the catalogue is stuck in map until a reload, with no
      way to tell it was the window that did it. On a laptop that
      reads exactly like "it keeps opening in map".

      So the view the visitor actually asked for is now tracked
      separately from the view being displayed:

        chosen        what they picked, or what the template
                      opened with. Only a click on the mode
                      buttons changes it.
        forcedByWidth true while the phone rule is overriding
                      that choice.

      applyWidth() forces the phone view on the way down and puts
      `chosen` back on the way up, and it is the single place both
      resize and orientationchange call. Do not go back to reading
      mode() to decide: mode() is the symptom, chosen is the
      intent, and the bug was confusing the two.

      v9 closes the last hole in this: boot() now sets
      forcedByWidth itself when it opens a phone on the override.
      Before, a phone that started narrow and was then widened,
      which is a tablet rotating, hit the same one way door,
      because forcedByWidth had never been set true in the first
      place. Only the resize path had ever set it.

   8. THE PHONE DEFAULT IS GRID, NOT MAP. v9, 2026-09-12.
      Harpreet's call, and it reverses what rule 3 used to land on.

      Rule 3 says split cannot exist on a phone. It never said the
      fallback had to be the map, and the map was the wrong half
      to keep. Measured at 375px: a 420px tall map carrying nine
      pins sits above the fold and the parcel cards start below
      it. A pin shows a price and nothing else until it is tapped.
      A grid card shows price, acreage, county and access without
      a tap. With a catalogue this size the map earns very little
      as an opening surface, and it costs a Leaflet boot plus a
      tile fetch before anything is readable.

      It also contradicted the standing rule that the map is a
      tool the buyer opts into, never the thing standing between
      them and the listing.

      PHONE_VIEW is the single place this is decided. It reads
      CFG.phoneView so the section can offer it as a setting
      later, and falls back to grid when the liquid does not send
      one, which is the case today. Map is still one tap away and
      a visitor who taps it keeps it, because a click sets
      `chosen` and rule 7 then protects it.

   9. BROWSE STATE IS WRITTEN INTO THE URL. v9, 2026-09-12.

      Until now nothing about a search survived leaving this page.
      Filters, sort, view and map position lived in memory only:
      no query string, nothing in localStorage, nothing in
      sessionStorage. Measured 2026-09-12, applying State:
      Florida narrowed the count to 6 and left the URL clean.

      That is what made the back control on a parcel page return
      people to a reset catalogue instead of to their own search,
      and it is why coming back felt slow. It was not resuming, it
      was re-booting Leaflet, re-fetching tiles and re-fitting the
      frame from scratch every single time.

      replaceState, NOT pushState. pushState would put one history
      entry behind every dropdown change, so the browser back
      button would step backwards through a visitor's own filters
      instead of leaving the page. replaceState keeps the address
      bar honest and the history clean, and because the Referer
      header is taken from the current document URL, the parcel
      page's back control picks the state up for free.

      PARAMS THIS FILE OWNS are listed in MINE. Everything else in
      the query string is preserved untouched on every write. That
      is not decoration: preview_theme_id lives there, and wiping
      it would break theme previews the moment anyone touched a
      filter. utm tags survive for the same reason.

      A VALUE IS ONLY ACCEPTED IF THE CONTROL OFFERS IT. readURL
      checks each incoming value against the select's own options
      before assigning it, so a hand edited or stale link cannot
      put the UI into a state the catalogue cannot represent. A
      county that no longer exists is ignored rather than
      producing a silent zero result page.

      view IS ONLY WRITTEN IF A HUMAN PICKED ONE. viewTouched is
      set by the mode buttons and by nothing else. Without that,
      a phone would stamp view=grid onto every link it produced
      purely because grid is the phone default, and that link
      opened on a desktop would force grid there too.

      ll AND z ARE ONLY WRITTEN IF THE VISITOR MOVED THE MAP.
      userMoved already tracks that. Reset view clears it, which
      correctly drops the position back out of the URL.

   FRAMING RULES THAT MUST NOT COME BACK AS BUGS:
     a. sizeUp calls invalidateSize THEN refits, guarded by
        userMoved so it never yanks the map from under someone
        who has panned.
     b. zoomSnap 0.25. Whole steps made a fit wanting 5.6 land on
        5 and fill half the pane.
     c. Pixel padding, not bounds.pad().
     d. Esri World Imagery is {z}/{y}/{x}. y before x.

   OPENING VIEW is the region holding the most parcels, not all
   of them. Fitting Illinois and Florida in one frame zooms out
   so far that every parcel falls inside the cluster radius, so
   you get anonymous count bubbles and no prices on a map that is
   mostly ocean. A restored position from the URL overrides this,
   via redraw('none'), because the visitor's own frame beats an
   opening guess.

   THAT OPENING FRAME IS ALSO WHY "Search as I move" DEFAULTS
   OFF in the section settings. With it on at load, the list
   answered to a frame that deliberately holds only the biggest
   region, so the three Illinois parcels vanished from a page
   whose own heading said nine. The toggle is still there and
   still works. It just is not the state a first time visitor
   lands in. */
(function(){
  var root = document.querySelector('.fmap');
  if(!root || typeof L === 'undefined' || !window.FLG_MAP) return;

  var CFG = window.FLG_MAP, ITEMS = CFG.items || [], VIEW = CFG.viewLabel || 'View details';
  var map, cluster, markers = {}, allPts = [], booted = false;
  var lastFit = null, fitting = false, userMoved = false;
  var FKEYS = ['fm-state','fm-county','fm-price','fm-acres','fm-access','fm-status'];
  var countEl, expandEl, filtEl;

  /* Rule 7. Intent, kept apart from what is on screen. */
  var chosen = root.getAttribute('data-mode') || 'split';
  var forcedByWidth = false;

  /* Rule 8. The one place the phone fallback is decided. */
  var PHONE_VIEW = (CFG.phoneView === 'map') ? 'map' : 'grid';

  /* Rule 9. */
  var URL_STATE = CFG.urlState !== false;
  var viewTouched = false;
  var restoredPos = null;
  var urlT = null;

  var $  = function(s){ return root.querySelector(s); };
  var $$ = function(s){ return Array.prototype.slice.call(root.querySelectorAll(s)); };
  var isPhone = function(){ return window.matchMedia('(max-width:750px)').matches; };
  var mode = function(){ return root.getAttribute('data-mode') || 'split'; };

  /* ---------- controls the liquid does not ship. Rule 5. ---------- */
  (function buildUI(){
    var barin = $('.barin'), modes = $('.modes'), live = $('.live'), clr = $('.clr');
    if(!barin) return;

    if(modes && modes.nextSibling){
      var rule = document.createElement('span');
      rule.className = 'rule';
      barin.insertBefore(rule, modes.nextSibling);
    }

    /* right hand group first, because moving the sort out of the
       bar is what leaves exactly the filters behind. */
    var right = document.createElement('div');
    right.className = 'right';
    countEl = document.createElement('p');
    countEl.className = 'count';
    right.appendChild(countEl);
    var sort = document.getElementById('fm-sort');
    if(sort && sort.parentNode) right.appendChild(sort.parentNode);
    if(live) right.appendChild(live);
    barin.appendChild(right);
    if(clr) barin.insertBefore(clr, right);

    /* now wrap what is left. display:contents keeps the desktop
       row identical; the phone sheet turns the wrapper on. */
    var flds = Array.prototype.slice.call(barin.children).filter(function(el){
      return el.classList && el.classList.contains('fld');
    });
    if(flds.length){
      var box = document.createElement('div');
      box.className = 'filters';
      flds[0].parentNode.insertBefore(box, flds[0]);
      flds.forEach(function(f){ box.appendChild(f); });

      filtEl = document.createElement('button');
      filtEl.type = 'button';
      filtEl.className = 'filt';
      filtEl.setAttribute('aria-expanded', 'false');
      box.parentNode.insertBefore(filtEl, box);
      filtEl.addEventListener('click', function(){
        var open = root.classList.toggle('filters-open');
        filtEl.classList.toggle('on', open);
        filtEl.setAttribute('aria-expanded', open ? 'true' : 'false');
        setTimeout(sizeUp, 40);
      });
    }

    var wrap = $('.mapwrap');
    if(wrap){
      expandEl = document.createElement('button');
      expandEl.type = 'button';
      expandEl.className = 'expand';
      expandEl.textContent = CFG.expandLabel || 'Expand';
      wrap.appendChild(expandEl);
    }
  })();

  function bottomBar(){
    var bars = document.querySelectorAll('.dock, .flg-dock, [class*="mobile-dock"]');
    var tallest = 0;
    for(var i = 0; i < bars.length; i++){
      var el = bars[i], cs = getComputedStyle(el);
      if(cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden') continue;
      var b = el.getBoundingClientRect();
      if(b.height < 1) continue;
      if(b.bottom >= window.innerHeight - 2 && b.height > tallest) tallest = b.height;
    }
    return Math.round(tallest);
  }

  function region(it){
    if(!it.cty) return 'other';
    var bits = String(it.cty).split(',');
    return bits[bits.length - 1].trim() || 'other';
  }

  function biggestRegion(list){
    var by = {};
    list.forEach(function(it){
      if(it.lat === null) return;
      var r = region(it);
      (by[r] = by[r] || []).push([it.lat, it.lng]);
    });
    var best = null;
    Object.keys(by).forEach(function(k){ if(!best || by[k].length > by[best].length) best = k; });
    return best ? by[best] : [];
  }

  function fit(pts){
    if(!map || !pts || !pts.length) return;
    lastFit = pts;
    fitting = true;
    map.fitBounds(L.latLngBounds(pts), {
      animate: false, padding: [58, 58], maxZoom: pts.length === 1 ? 12 : 15
    });
    setTimeout(function(){ fitting = false; }, 0);
  }

  /* Rule 1. Read the header before changing a single term. */
  function sizeUp(){
    var split = $('.split');
    if(!split) return;
    if(mode() !== 'grid' && !root.classList.contains('is-full')){
      var docTop = split.getBoundingClientRect().top + window.scrollY;
      var cap = window.innerHeight * 0.45;
      var h = window.innerHeight - Math.min(docTop, cap) - bottomBar();
      root.style.setProperty('--fm-h', Math.max(420, Math.round(h)) + 'px');
    }
    if(map){
      map.invalidateSize();
      if(!userMoved && lastFit) fit(lastFit);
    }
  }

  function pill(n){
    if(!n && n !== 0) return CFG.noPrice || 'Ask';
    if(n >= 1000){ var k = n / 1000; return '$' + (k >= 100 ? Math.round(k) : Math.round(k * 10) / 10) + 'k'; }
    return '$' + n;
  }

  function band(v, kind){
    if(v === null) return 'na';
    if(kind === 'price'){ return v < 10000 ? 'a' : v < 20000 ? 'b' : v < 35000 ? 'c' : 'd'; }
    return v < 0.25 ? 'a' : v < 0.5 ? 'b' : v < 1 ? 'c' : 'd';
  }
  function val(id){ var e = document.getElementById(id); return e ? e.value : ''; }

  /* ---------- Rule 9. URL state. ----------
     MINE is the contract: these keys belong to this file and are
     rewritten on every save. Anything else in the query string is
     carried through untouched, which is what keeps
     preview_theme_id and utm tags alive. */
  var URLMAP = [
    ['fm-state',  'st'],
    ['fm-county', 'cty'],
    ['fm-price',  'p'],
    ['fm-acres',  'a'],
    ['fm-access', 'acc'],
    ['fm-status', 's'],
    ['fm-sort',   'sort']
  ];
  var MINE = ['st','cty','p','a','acc','s','sort','view','ll','z','live'];

  function readURL(){
    if(!URL_STATE || typeof URLSearchParams !== 'function') return;
    var q;
    try { q = new URLSearchParams(window.location.search); } catch(e){ return; }

    URLMAP.forEach(function(pair){
      var el = document.getElementById(pair[0]);
      if(!el) return;
      var v = q.get(pair[1]);
      if(v === null) return;
      /* only accept something this control actually offers */
      var ok = Array.prototype.some.call(el.options || [], function(o){ return o.value === v; });
      if(ok) el.value = v;
    });

    var v = q.get('view');
    if(v === 'grid' || v === 'map' || v === 'split'){
      chosen = v;
      viewTouched = true;
    }

    var live = document.getElementById('fm-live');
    if(live && q.get('live') === '1') live.checked = true;

    var ll = q.get('ll'), z = q.get('z');
    if(ll && z){
      var bits = String(ll).split(',');
      var la = parseFloat(bits[0]), ln = parseFloat(bits[1]), zz = parseFloat(z);
      if(isFinite(la) && isFinite(ln) && isFinite(zz) &&
         la >= -90 && la <= 90 && ln >= -180 && ln <= 180 && zz >= 1 && zz <= 20){
        restoredPos = [la, ln, zz];
      }
    }
  }

  function writeURL(){
    if(!URL_STATE || !booted || typeof URLSearchParams !== 'function') return;
    var q;
    try { q = new URLSearchParams(window.location.search); } catch(e){ return; }

    MINE.forEach(function(k){ q.delete(k); });

    URLMAP.forEach(function(pair){
      var el = document.getElementById(pair[0]);
      if(el && el.value) q.set(pair[1], el.value);
    });

    if(viewTouched) q.set('view', mode());

    var live = document.getElementById('fm-live');
    if(live && live.checked) q.set('live', '1');

    if(map && userMoved && mode() !== 'grid'){
      var c = map.getCenter();
      q.set('ll', c.lat.toFixed(5) + ',' + c.lng.toFixed(5));
      q.set('z', String(Math.round(map.getZoom() * 4) / 4));
    }

    var s = q.toString();
    var next = window.location.pathname + (s ? '?' + s : '') + window.location.hash;
    var now  = window.location.pathname + window.location.search + window.location.hash;
    if(next === now) return;
    try { history.replaceState(history.state, '', next); } catch(e){}
  }

  function queueURL(){
    if(!URL_STATE) return;
    clearTimeout(urlT);
    urlT = setTimeout(writeURL, 250);
  }

  /* Rule 6. Keep County honest about the chosen State. Runs on
     every state change and once at boot, because a browser can
     restore a selected option on a soft reload. */
  function syncCounties(){
    var cSel = document.getElementById('fm-county');
    if(!cSel) return;
    var s = val('fm-state');
    var opts = Array.prototype.slice.call(cSel.options);
    opts.forEach(function(o){
      if(!o.value) return;                       /* the label option always stays */
      var keep = !s || o.getAttribute('data-st') === s;
      o.hidden = !keep;
      o.style.display = keep ? '' : 'none';      /* Safari ignores hidden on option */
    });
    var cur = cSel.selectedOptions && cSel.selectedOptions[0];
    if(cur && cur.value && cur.hidden) cSel.value = '';
  }

  function matching(){
    var f = { state: val('fm-state'), county: val('fm-county'), price: val('fm-price'),
              acres: val('fm-acres'), access: val('fm-access'), status: val('fm-status') };
    var out = ITEMS.filter(function(it){
      /* A parcel with no county has no state. It is never removed
         by the state filter, for the same reason a parcel with no
         gps still appears in the rail: the catalogue and the
         filters have to agree on what exists. */
      if(f.state  && it.stt && it.stt !== f.state) return false;
      if(f.county && it.cty !== f.county) return false;
      if(f.access && it.acc !== f.access) return false;
      if(f.status && it.st  !== f.status) return false;
      if(f.price  && band(it.pn, 'price') !== f.price) return false;
      if(f.acres  && band(it.ac, 'acres') !== f.acres) return false;
      return true;
    });
    var s = val('fm-sort');
    if(s === 'plow')  out.sort(function(a,b){ return (a.pn===null)-(b.pn===null) || a.pn-b.pn; });
    if(s === 'phigh') out.sort(function(a,b){ return (a.pn===null)-(b.pn===null) || b.pn-a.pn; });
    if(s === 'big')   out.sort(function(a,b){ return (a.ac===null)-(b.ac===null) || b.ac-a.ac; });
    if(s === 'new')   out.sort(function(a,b){ return b.d - a.d; });
    return out;
  }

  /* Rule 2. */
  function inView(list){
    if(mode() === 'grid') return list;
    var live = $('#fm-live');
    if(!map || !live || !live.checked || !booted) return list;
    var b = map.getBounds();
    return list.filter(function(it){ return it.lat === null ? false : b.contains([it.lat, it.lng]); });
  }

  function cardHTML(it){
    var img = it.img ? '<img src="' + it.img + '" alt="" loading="lazy" width="320" height="240">' : '';
    var cls = it.st === 'hold' ? ' is-hold' : (it.st === 'closed' ? ' is-closed' : '');
    var tag = it.st === 'hold' ? CFG.tagHold : (it.st === 'closed' ? CFG.tagClosed : CFG.tagOpen);
    var facts = '';
    if(it.acf) facts += '<span class="mi">' + it.acf + '</span>';
    if(it.acc) facts += '<span class="mi">' + it.acc + '</span>';
    var price = it.pf ? '<span class="amt">' + it.pf + '</span>'
                      : '<span class="none">' + (CFG.noPrice || 'Price on request') + '</span>';
    return '<a class="card" href="' + it.u + '" data-id="' + it.id + '">' +
      '<span class="shot">' + img + '<span class="tag' + cls + '">' + tag + '</span></span>' +
      '<span class="txt">' +
        (it.ctys ? '<span class="cty">' + it.ctys + '</span>' : '') +
        '<h3>' + it.t + '</h3>' +
        (facts ? '<span class="row">' + facts + '</span>' : '') +
        '<span class="foot">' + price + '<span class="go">' + VIEW + '</span></span>' +
      '</span></a>';
  }

  function paintRail(){
    var all = matching();
    var shown = inView(all);
    var word = shown.length === 1 ? CFG.wordOne : CFG.wordMany;

    /* the count is the most important number on a search UI, so
       it is in the bar as well as the rail. The rail header is
       invisible in map view and below the fold in grid. */
    if(countEl) countEl.innerHTML = shown.length + '<small>' + word + '</small>';

    var cnt = $('.railhd .cnt');
    if(cnt) cnt.innerHTML = '<b>' + shown.length + '</b> ' + word;
    var wrap = $('.cards');
    if(wrap) wrap.innerHTML = shown.map(cardHTML).join('');
    var end = $('.railend');
    if(end){
      var live = $('#fm-live');
      var moving = mode() !== 'grid' && live && live.checked;
      end.textContent = shown.length ? (moving ? CFG.moveNote : CFG.endNote)
                                     : (moving ? CFG.emptyView : CFG.emptyText);
    }
    $$('.card').forEach(function(c){
      c.addEventListener('mouseenter', function(){ if(!isPhone() && mode() !== 'grid') focus(c.dataset.id, false); });
    });
    var none = $('.empty');
    if(none) none.textContent = all.length ? '' : CFG.emptyText;
  }

  function paintMap(refit){
    var list = matching();
    if(cluster) cluster.clearLayers();
    markers = {};
    allPts = [];
    var batch = [];

    list.forEach(function(it){
      if(it.lat === null || it.lng === null) return;
      var cls = 'pin' + (it.st === 'hold' ? ' is-hold' : (it.st === 'closed' ? ' is-closed' : ''));
      var m = L.marker([it.lat, it.lng], {
        icon: L.divIcon({ className: cls, html: '<b>' + pill(it.pn) + '</b>', iconSize: [0,0] }),
        title: it.t, riseOnHover: true
      });
      m.bindPopup(cardHTML(it), { closeButton:true, offset:[0,-12], minWidth:252, maxWidth:252, autoPanPadding:[30,30] });
      m.on('click', function(){ focus(it.id, true); });
      batch.push(m);
      markers[it.id] = m;
      allPts.push([it.lat, it.lng]);
    });

    if(cluster && batch.length) cluster.addLayers(batch);

    /* 'none' is the restored-position path. Rule 9. */
    if(refit === 'all') fit(allPts);
    else if(refit === 'open') fit(CFG.openOn === 'all' ? allPts : biggestRegion(list));
  }

  function redraw(refit){
    if(refit && refit !== 'none') userMoved = false;
    paintMap(refit || 'all');
    paintRail();
    flagSet();
  }

  function focus(id, fromMap){
    $$('.card').forEach(function(c){ c.classList.toggle('lit', c.dataset.id === String(id)); });
    Object.keys(markers).forEach(function(k){
      var el = markers[k]._icon;
      if(!el) return;
      el.classList.toggle('on', k === String(id));
      el.classList.toggle('lit', k === String(id));
    });
    if(fromMap){
      var m = markers[id];
      if(m && m._icon) m._icon.classList.add('seen');
      if(!isPhone() && mode() === 'split'){
        var card = root.querySelector('.card[data-id="' + id + '"]');
        var rail = $('.rail');
        if(card && rail) rail.scrollTo({ top: card.offsetTop - 70, behavior: 'smooth' });
      }
    }
  }

  /* Clear and the phone Filters button both say how many filters
     are on, so the bar tells you how filtered you are without
     you reading six dropdowns. */
  function flagSet(){
    var n = 0;
    FKEYS.forEach(function(id){
      var e = document.getElementById(id);
      if(!e) return;
      var on = !!e.value;
      e.classList.toggle('set', on);
      if(on) n++;
    });
    var c = $('.clr');
    if(c){
      c.hidden = n === 0;
      if(n) c.textContent = (CFG.clearLabel || 'Clear') + ' ' + n;
    }
    if(filtEl){
      filtEl.innerHTML = (CFG.filtersLabel || 'Filters') + (n ? ' <b>' + n + '</b>' : '');
    }
  }

  function setMode(m){
    /* Rules 3 and 8. */
    if(m === 'split' && isPhone()) m = PHONE_VIEW;
    root.setAttribute('data-mode', m);
    $$('.modes button').forEach(function(b){ b.classList.toggle('on', b.dataset.mode === m); });
    if(m === 'grid'){ paintRail(); return; }
    setTimeout(function(){ sizeUp(); paintRail(); }, 30);
  }

  /* Rule 7. The only place the width is allowed to change the
     view, and the only place it is allowed to change it back. */
  function applyWidth(){
    if(isPhone()){
      if(chosen === 'split' && mode() !== PHONE_VIEW){
        forcedByWidth = true;
        setMode(PHONE_VIEW);
        return;
      }
    } else if(forcedByWidth){
      forcedByWidth = false;
      setMode(chosen);
      return;
    }
    sizeUp();
  }

  function setFull(on){
    root.classList.toggle('is-full', on);
    document.documentElement.style.overflow = on ? 'hidden' : '';
    if(expandEl) expandEl.textContent = on ? (CFG.collapseLabel || 'Close') : (CFG.expandLabel || 'Expand');
    setTimeout(function(){ if(map) map.invalidateSize(); sizeUp(); }, 60);
  }

  var BASE = {
    satellite: { url:'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                 opts:{ maxZoom:19, attribution:'Imagery: Esri, Maxar, Earthstar Geographics' } },
    street:    { url:'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                 opts:{ maxZoom:19, attribution:'&copy; OpenStreetMap contributors' } },
    terrain:   { url:'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
                 opts:{ maxZoom:17, attribution:'Map data: &copy; OpenStreetMap, SRTM. Style: &copy; OpenTopoMap' } }
  };
  var tiles = null;
  function base(name){
    if(!BASE[name]) name = 'satellite';
    if(tiles) map.removeLayer(tiles);
    tiles = L.tileLayer(BASE[name].url, BASE[name].opts).addTo(map);
    tiles.bringToBack();
    $$('.basemaps button').forEach(function(b){ b.classList.toggle('on', b.dataset.base === name); });
  }

  function boot(){
    var el = $('.map');
    if(!el) return;

    map = L.map(el, { scrollWheelZoom:false, zoomControl:true, zoomSnap:0.25, zoomDelta:0.5, minZoom:3 })
           .setView([28.6, -82.4], 6);
    base(CFG.base || 'satellite');

    cluster = (typeof L.markerClusterGroup === 'function')
      ? L.markerClusterGroup({
          showCoverageOnHover:false, spiderfyOnMaxZoom:true, maxClusterRadius:30,
          iconCreateFunction: function(c){
            var n = c.getChildCount();
            return L.divIcon({ className:'clu' + (n > 9 ? ' big' : ''), html:'<i>' + n + '</i>', iconSize:[0,0] });
          }
        })
      : L.layerGroup();
    map.addLayer(cluster);

    el.addEventListener('wheel', function(e){
      if(e.ctrlKey || e.metaKey){ e.preventDefault(); map.scrollWheelZoom.enable(); }
      else { map.scrollWheelZoom.disable(); }
    }, { passive:false });

    map.on('dragstart', function(){ userMoved = true; });
    map.on('zoomstart', function(){ if(!fitting) userMoved = true; });

    $$('.basemaps button').forEach(function(b){
      b.addEventListener('click', function(){ base(b.dataset.base); });
    });

    FKEYS.concat(['fm-sort']).forEach(function(id){
      var s = document.getElementById(id);
      if(!s) return;
      s.addEventListener('change', function(){
        /* Rule 6. County has to follow State before anything is
           counted, or the count is taken against a county the
           chosen state does not contain. */
        if(id === 'fm-state') syncCounties();
        redraw('all');
        writeURL();
      });
    });

    var clr = $('.clr');
    if(clr) clr.addEventListener('click', function(){
      FKEYS.concat(['fm-sort']).forEach(function(id){
        var s = document.getElementById(id); if(s) s.value = '';
      });
      syncCounties();
      redraw('open');
      writeURL();
    });
    var live = $('#fm-live');
    if(live) live.addEventListener('change', function(){ paintRail(); writeURL(); });
    var refit = $('.refit');
    if(refit) refit.addEventListener('click', function(){
      userMoved = false;
      fit(allPts);
      writeURL();
    });

    if(expandEl) expandEl.addEventListener('click', function(){ setFull(!root.classList.contains('is-full')); });
    document.addEventListener('keydown', function(e){
      if(e.key !== 'Escape') return;
      if(root.classList.contains('is-full')) setFull(false);
      else if(root.classList.contains('filters-open') && filtEl) filtEl.click();
    });

    /* Rule 7. A click is the ONLY thing that changes intent.
       Rule 9. It is also the only thing that puts view in the URL. */
    $$('.modes button').forEach(function(b){
      b.addEventListener('click', function(){
        chosen = b.dataset.mode;
        forcedByWidth = false;
        viewTouched = true;
        setMode(chosen);
        writeURL();
      });
    });

    map.on('moveend zoomend', function(){
      if(booted && mode() !== 'grid') paintRail();
      queueURL();
    });

    /* Rule 9. The URL is read before anything is painted, so the
       first frame a returning visitor sees is already their own
       search rather than a default that flickers into it. */
    readURL();
    syncCounties();

    /* Rule 7, v9 hole. If the phone rule is about to override the
       template's opening view, say so now, or widening back later
       has nothing to undo. */
    if(isPhone() && chosen === 'split') forcedByWidth = true;

    setMode(chosen);
    sizeUp();

    if(restoredPos){
      userMoved = true;
      map.setView([restoredPos[0], restoredPos[1]], restoredPos[2], { animate:false });
      redraw('none');
    } else {
      redraw('open');
    }

    booted = true;
    paintRail();

    [140, 420, 1000].forEach(function(ms){ setTimeout(function(){ sizeUp(); }, ms); });
    window.addEventListener('load', sizeUp);
    if(document.fonts && document.fonts.ready){ document.fonts.ready.then(sizeUp); }

    var t;
    window.addEventListener('resize', function(){
      clearTimeout(t);
      t = setTimeout(applyWidth, 120);
    });
    window.addEventListener('orientationchange', function(){
      setTimeout(applyWidth, 320);
    });
    if(window.ResizeObserver){
      new ResizeObserver(function(){ if(map && mode() !== 'grid') map.invalidateSize(); }).observe($('.mapwrap'));
    }
  }

  function ready(){
    if(typeof L.markerClusterGroup === 'function' || ready.tries++ > 40) return boot();
    setTimeout(ready, 50);
  }
  ready.tries = 0;

  if(document.readyState === 'loading'){ document.addEventListener('DOMContentLoaded', ready); }
  else { ready(); }
})();
